import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { AdminHeader } from "../components/AdminNav";
import {
    adminGetEmailSettings,
    adminMe,
    adminTestEmail,
    adminUpdateEmailSettings,
} from "../lib/api";

const blank = {
    provider: "resend",
    api_key: "",
    sender_email: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    recipient: "",
    cc: [],
    bcc: [],
    sender_name: "ASC Interior",
    reply_to: "",
    subject_template: "New {form_type} inquiry from {name}",
    last_test_at: null,
    last_test_ok: false,
    last_test_message: null,
};

export default function AdminEmailSettings() {
    const [form, setForm] = useState(null);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [ccText, setCcText] = useState("");
    const [bccText, setBccText] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    const load = () =>
        adminGetEmailSettings()
            .then((doc) => {
                setForm({ ...blank, ...doc });
                setCcText((doc.cc || []).join(", "));
                setBccText((doc.bcc || []).join(", "));
            })
            .catch((err) => setError(err?.response?.data?.detail || "Load failed"));

    useEffect(() => {
        load();
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = {
                ...form,
                cc: ccText.split(",").map((s) => s.trim()).filter(Boolean),
                bcc: bccText.split(",").map((s) => s.trim()).filter(Boolean),
                smtp_port: Number(form.smtp_port) || 587,
            };
            const saved = await adminUpdateEmailSettings(payload);
            setForm({ ...blank, ...saved });
            setSavedAt(new Date());
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        setError("");
        setTestResult(null);
        setTesting(true);
        try {
            const res = await adminTestEmail();
            setTestResult(res);
            await load();
        } catch (err) {
            setTestResult({
                ok: false,
                message: err?.response?.data?.detail || String(err),
            });
        } finally {
            setTesting(false);
        }
    };

    if (!form) {
        return (
            <main className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16">
                <p className="asc-overline">Loading…</p>
                {error && <p className="text-red-700 text-sm mt-4">{error}</p>}
            </main>
        );
    }

    const isResend = form.provider === "resend";
    const isSmtp = form.provider === "smtp";
    const connected = form.last_test_ok;

    return (
        <main
            data-testid="page-admin-email"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/settings/email" title="Email Settings — Admin" description="Manage inquiry email delivery" noindex />
            <AdminHeader title="Email settings" />

            <div className="mt-8 flex items-center gap-3" data-testid="connection-status">
                <span
                    className={`inline-block w-2.5 h-2.5 rounded-full ${
                        connected ? "bg-emerald-500" : "bg-[#8C7A6B]"
                    }`}
                />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#5C5855]">
                    {connected ? "\u2713 Connected" : "Not connected"}
                    {form.last_test_at && (
                        <span className="ml-3 lowercase tracking-normal text-[#A89F94] normal-case">
                            — last test {new Date(form.last_test_at).toLocaleString()}
                        </span>
                    )}
                </span>
            </div>

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="email-settings-error">{error}</p>}
            {savedAt && <p className="mt-6 text-green-700 text-sm" data-testid="email-settings-saved">Saved at {savedAt.toLocaleTimeString()}.</p>}

            {/* -------- Setup guide -------- */}
            <section className="mt-10 max-w-3xl grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="setup-guide">
                <div className={`border p-6 ${isResend ? "border-[#1A1A1A] bg-white" : "border-[#E5E1DB] bg-white/60"}`}>
                    <div className="flex items-center justify-between">
                        <p className="asc-overline">Option A · Recommended</p>
                        <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 bg-[#E0CFB9] text-[#1A1A1A]">Resend</span>
                    </div>
                    <h3 className="serif text-2xl mt-3 tracking-tight font-light">Managed email delivery</h3>
                    <ol className="mt-4 text-sm text-[#5C5855] leading-relaxed list-decimal pl-5 space-y-1">
                        <li>Create a free account at <a href="https://resend.com" target="_blank" rel="noreferrer" className="asc-link">resend.com</a>.</li>
                        <li>Verify your sending domain (Domains → Add → follow DNS steps).</li>
                        <li>Generate an API Key (API Keys → Create).</li>
                        <li>Paste the key below (starts with <code>re_</code>).</li>
                        <li>Click <em>Send Test Email</em> to confirm delivery.</li>
                    </ol>
                    <p className="mt-4 text-xs text-[#8C7A6B]">Required fields: Provider · API Key · Sender Email · Recipient Email.</p>
                </div>
                <div className={`border p-6 ${isSmtp ? "border-[#1A1A1A] bg-white" : "border-[#E5E1DB] bg-white/60"}`}>
                    <div className="flex items-center justify-between">
                        <p className="asc-overline">Option B</p>
                        <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 bg-[#E5E1DB] text-[#1A1A1A]">SMTP</span>
                    </div>
                    <h3 className="serif text-2xl mt-3 tracking-tight font-light">Any SMTP server</h3>
                    <ol className="mt-4 text-sm text-[#5C5855] leading-relaxed list-decimal pl-5 space-y-1">
                        <li>Get SMTP credentials from your provider (Gmail app password, SendGrid, Postmark, Office 365, etc.).</li>
                        <li>Paste host, port (usually 587 STARTTLS or 465 SSL), username and password.</li>
                        <li>Set the sender email — usually the same as the SMTP username.</li>
                        <li>Click <em>Send Test Email</em> to confirm delivery.</li>
                    </ol>
                    <p className="mt-4 text-xs text-[#8C7A6B]">Required: SMTP Host · Port · Username · Password · Sender Email.</p>
                </div>
            </section>

            {/* -------- Settings form -------- */}
            <form onSubmit={save} className="mt-10 max-w-2xl space-y-6">
                <p className="asc-overline">Provider</p>
                <div className="flex gap-3" data-testid="provider-selector">
                    {[
                        { key: "resend", label: "Resend (Recommended)" },
                        { key: "smtp", label: "SMTP" },
                    ].map((opt) => (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => set("provider", opt.key)}
                            data-testid={`provider-${opt.key}`}
                            className={`px-4 py-3 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                form.provider === opt.key
                                    ? "bg-[#1A1A1A] text-[#F9F8F6] border-[#1A1A1A]"
                                    : "bg-white text-[#5C5855] border-[#E5E1DB] hover:border-[#1A1A1A]"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                {isResend && (
                    <Field label="Resend API Key" hint="Starts with `re_`. Stored securely in MongoDB.">
                        <input
                            data-testid="email-api-key"
                            type="password"
                            value={form.api_key}
                            onChange={(e) => set("api_key", e.target.value)}
                            placeholder="re_XXXXXXXXXXXXXXXXX"
                            className={inputCls}
                            autoComplete="off"
                        />
                    </Field>
                )}

                {isSmtp && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Field label="SMTP Host">
                                <input
                                    data-testid="smtp-host"
                                    value={form.smtp_host}
                                    onChange={(e) => set("smtp_host", e.target.value)}
                                    placeholder="smtp.gmail.com"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Port">
                                <input
                                    data-testid="smtp-port"
                                    type="number"
                                    value={form.smtp_port}
                                    onChange={(e) => set("smtp_port", e.target.value)}
                                    className={inputCls}
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Field label="Username">
                                <input
                                    data-testid="smtp-username"
                                    value={form.smtp_username}
                                    onChange={(e) => set("smtp_username", e.target.value)}
                                    className={inputCls}
                                    autoComplete="off"
                                />
                            </Field>
                            <Field label="Password">
                                <input
                                    data-testid="smtp-password"
                                    type="password"
                                    value={form.smtp_password}
                                    onChange={(e) => set("smtp_password", e.target.value)}
                                    className={inputCls}
                                    autoComplete="off"
                                />
                            </Field>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                            <input
                                type="checkbox"
                                data-testid="smtp-use-tls"
                                checked={!!form.smtp_use_tls}
                                onChange={(e) => set("smtp_use_tls", e.target.checked)}
                            />
                            Use STARTTLS (recommended for port 587)
                        </label>
                    </>
                )}

                <Field label="Sender Email">
                    <input
                        data-testid="sender-email"
                        value={form.sender_email}
                        onChange={(e) => set("sender_email", e.target.value)}
                        placeholder="info@ascinterior.com"
                        className={inputCls}
                    />
                </Field>
                <Field label="Sender Name">
                    <input
                        data-testid="sender-name"
                        value={form.sender_name}
                        onChange={(e) => set("sender_name", e.target.value)}
                        className={inputCls}
                    />
                </Field>
                <Field label="Recipient Email" hint="Every inquiry is delivered here.">
                    <input
                        data-testid="email-recipient"
                        value={form.recipient}
                        onChange={(e) => set("recipient", e.target.value)}
                        placeholder="info@ascinterior.com"
                        className={inputCls}
                    />
                </Field>
                <Field label="Cc" hint="Comma-separated list.">
                    <input
                        data-testid="email-cc"
                        value={ccText}
                        onChange={(e) => setCcText(e.target.value)}
                        className={inputCls}
                    />
                </Field>
                <Field label="Bcc" hint="Comma-separated list.">
                    <input
                        data-testid="email-bcc"
                        value={bccText}
                        onChange={(e) => setBccText(e.target.value)}
                        className={inputCls}
                    />
                </Field>
                <Field label="Reply-to Email" hint="Leave blank to reply directly to the submitter.">
                    <input
                        data-testid="email-reply-to"
                        value={form.reply_to}
                        onChange={(e) => set("reply_to", e.target.value)}
                        className={inputCls}
                    />
                </Field>
                <Field label="Subject Template" hint={"Supports {form_type} and {name} placeholders."}>
                    <input
                        data-testid="email-subject"
                        value={form.subject_template}
                        onChange={(e) => set("subject_template", e.target.value)}
                        className={inputCls}
                    />
                </Field>

                <div className="flex flex-wrap items-center gap-4 pt-6 border-t border-[#E5E1DB]">
                    <button
                        type="submit"
                        data-testid="email-save"
                        disabled={saving}
                        className="px-6 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save settings"}
                    </button>
                    <button
                        type="button"
                        onClick={runTest}
                        data-testid="send-test-email"
                        disabled={testing}
                        className="px-6 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-[11px] uppercase tracking-[0.22em] hover:bg-[#1A1A1A] hover:text-[#F9F8F6] disabled:opacity-50"
                    >
                        {testing ? "Sending…" : "Send Test Email"}
                    </button>
                </div>
                {testResult && (
                    <p
                        data-testid="test-result"
                        className={`mt-2 text-sm ${testResult.ok ? "text-emerald-700" : "text-red-700"}`}
                    >
                        {testResult.ok ? "\u2713 " : "\u2715 "} {testResult.message}
                    </p>
                )}
            </form>
        </main>
    );
}

const inputCls = "w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm text-[#1A1A1A]";

const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
            {label}
            {hint && <span className="ml-2 lowercase tracking-normal text-[#A89F94] normal-case">— {hint}</span>}
        </span>
        {children}
    </label>
);
